/**
 * AnimatedPlayersOverlay - SVG-based player rendering for animation mode.
 *
 * Uses PlayerVisual from the unified block architecture for WYSIWYG
 * consistency between editor and animation views.
 */

import { useMemo } from 'react'
import { BlockRenderProvider } from '@/components/blocks/BlockRenderContext'
import { PlayerVisual } from '@/components/blocks/visuals/canvas/PlayerVisual'
import { createPlayerBlock } from '@/adapters/PlayerAdapter'
import { createPixelScaleFactors } from '@/utils/RenderScale'
import type { BlockRenderContextValue } from '@/components/blocks/Types'
import type { FieldCoordinateSystem } from '@/utils/Coordinates'
import type { Coordinate } from '@/types'

interface PlayerState {
	playerId: string
	currentPosition: Coordinate
}

interface AnimatedPlayersOverlayProps {
	playerStates: PlayerState[]
	coordSystem: FieldCoordinateSystem
	playerInfo: Map<string, { label: string; color: string }>
	viewBox: { width: number; height: number }
}

export function AnimatedPlayersOverlay({
	playerStates,
	coordSystem,
	playerInfo,
	viewBox,
}: AnimatedPlayersOverlayProps) {
	const coordinateTransform = useMemo(
		() => (point: { x: number; y: number }) =>
			coordSystem.feetToPixels(point.x, point.y),
		[coordSystem],
	)

	const scale = useMemo(
		() => createPixelScaleFactors(coordSystem),
		[coordSystem.scale],
	)

	const contextValue: BlockRenderContextValue = useMemo(
		() => ({
			mode: 'animated' as const,
			zoom: 100,
			coordinateTransform,
			viewBox,
			scale,
		}),
		[coordinateTransform, viewBox, scale],
	)

	return (
		<BlockRenderProvider value={contextValue}>
			<g className="animated-players-overlay">
				{playerStates.map((playerState) => {
					const info = playerInfo.get(playerState.playerId)
					const block = createPlayerBlock(
						playerState.playerId,
						playerState.currentPosition,
						info,
					)
					return (
						<PlayerVisual
							key={playerState.playerId}
							block={block}
							animatedPosition={playerState.currentPosition}
						/>
					)
				})}
			</g>
		</BlockRenderProvider>
	)
}
